import { db } from './firebase';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy 
} from 'firebase/firestore';
import User from '../types/user';

const USERS_COLLECTION = 'users';
const APPLICATIONS_COLLECTION = 'jobApplications';

/**
 * User Management Services
 */
export const userServices = {
  /**
   * Adds a new user document to Firestore. Should be called immediately after sign up.
   * @param {User} user - An instance of the User class.
   * @returns {Promise<void>}
   */
  async addUserToFirestore(user) {
    const userRef = doc(db, USERS_COLLECTION, user.uid);
    return setDoc(userRef, user.toFirestore());
  },

  /**
   * Retrieves a user document from Firestore.
   * @param {string} uid - The Firebase Auth UID of the user.
   * @returns {Promise<User|null>}
   */
  async getUserFromFirestore(uid) {
    const userRef = doc(db, USERS_COLLECTION, uid);
    const docSnap = await getDoc(userRef);

    if (docSnap.exists()) {
      return User.fromFirestore(docSnap);
    }
    return null;
  },

  /**
   * Updates specific fields for a user in Firestore.
   * @param {string} uid - The Firebase Auth UID of the user.
   * @param {object} updates - An object containing fields to update.
   * @returns {Promise<void>}
   */
  async updateUserData(uid, updates) {
    const userRef = doc(db, USERS_COLLECTION, uid);
    const updateData = {
      ...updates,
      updatedAt: new Date()
    };
    return updateDoc(userRef, updateData);
  },

  /**
   * Updates user profile information
   * @param {string} uid - User ID
   * @param {object} profileData - Profile data to update
   * @returns {Promise<{success: boolean, error: string}>}
   */
  async updateUserProfile(uid, profileData) {
    try {
      const updates = {
        displayName: profileData.displayName,
        phoneNumber: profileData.phoneNumber,
        photoURL: profileData.photoURL,
        profileCompleted: true
      };

      await this.updateUserData(uid, updates);
      return { success: true };
    } catch (error) {
      console.error('Error updating user profile:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Handles the logic for a new sign-up, ensuring the user is added to Firestore.
   * @param {object} authUser - The Firebase Auth user object.
   * @returns {Promise<void>}
   */
  async handleNewSignUp(authUser) {
    const { uid, email, displayName, photoURL } = authUser;
    
    // Create a new User instance
    const newUser = new User({
      uid,
      email,
      displayName: displayName || '',
      photoURL: photoURL || '',
    });

    // Add to Firestore
    await this.addUserToFirestore(newUser);
  },

  /**
   * Get all users (Admin only)
   * @returns {Promise<Array>} - List of all users
   */
  async getAllUsers() {
    try {
      const q = query(
        collection(db, USERS_COLLECTION),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const users = [];

      querySnapshot.forEach((doc) => {
        users.push(User.fromFirestore(doc));
      });

      return users;
    } catch (error) {
      console.error('Error getting all users:', error);
      throw new Error(`Failed to get users: ${error.message}`);
    }
  },

  /**
   * Update user role (Admin only)
   * @param {string} uid - User ID
   * @param {string} role - New role ('user', 'admin', 'hr')
   * @returns {Promise<{success: boolean, error: string}>}
   */
  async updateUserRole(uid, role) {
    try {
      const validRoles = ['user', 'admin', 'hr'];
      if (!validRoles.includes(role)) {
        throw new Error(`Invalid role. Must be one of: ${validRoles.join(', ')}`);
      }

      await this.updateUserData(uid, { role });
      return { success: true };
    } catch (error) {
      console.error('Error updating user role:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

/**
 * User Applications Services
 */
export const userApplicationServices = {
  /**
   * Get user's job applications
   * @param {string} userId - User ID
   * @returns {Promise<Array>} - List of user's applications
   */
  async getUserApplications(userId) {
    try {
      const q = query(
        collection(db, APPLICATIONS_COLLECTION),
        where('userId', '==', userId),
        orderBy('appliedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const applications = [];

      querySnapshot.forEach((doc) => {
        applications.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return applications;
    } catch (error) {
      console.error('Error getting user applications:', error);
      throw new Error(`Failed to get applications: ${error.message}`);
    }
  },

  /**
   * Get application statistics for user dashboard
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Application statistics
   */
  async getUserApplicationStats(userId) {
    try {
      const applications = await this.getUserApplications(userId);
      
      const stats = {
        total: applications.length,
        pending: applications.filter(app => app.status === 'pending').length,
        reviewed: applications.filter(app => app.status === 'reviewed').length,
        accepted: applications.filter(app => app.status === 'accepted').length,
        rejected: applications.filter(app => app.status === 'rejected').length
      };

      return stats;
    } catch (error) {
      console.error('Error getting application stats:', error);
      throw new Error(`Failed to get application statistics: ${error.message}`);
    }
  }
};

/**
 * Combined service exports
 */
export const databaseServices = {
  ...userServices,
  applications: userApplicationServices
};

export default databaseServices;